import React, {useCallback, useEffect, useState} from 'react'
import {Spinner, Button} from 'reactstrap'
import {REMOVE_BG_STATUS} from '../../../hooks/useBackgroundRemoval'

function RemoveBgPreviewCanvas({
    canvasRef,
    containerRef,
    status,
    error,
    retry,
    ready,
    tool,
    mode,
    brushSize,
    zoom,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onWheel,
}) {
    const [mousePos, setMousePos] = useState(null)

    const handlePointerMove = useCallback(
        (e) => {
            const rect = containerRef?.current?.getBoundingClientRect()
            if (rect) {
                setMousePos({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                })
            }
            onPointerMove?.(e)
        },
        [containerRef, onPointerMove],
    )

    const handlePointerLeave = useCallback(() => {
        setMousePos(null)
    }, [])

    // Prevent default on wheel to avoid page scroll
    useEffect(() => {
        const container = containerRef?.current
        if (!container) return
        const preventWheel = (e) => e.preventDefault()
        container.addEventListener('wheel', preventWheel, {passive: false})
        return () => container.removeEventListener('wheel', preventWheel)
    }, [containerRef])

    const showBrushCursor = ready && tool === 'brush' && mousePos
    const cursorSize = brushSize * zoom

    return (
        <div
            ref={containerRef}
            className={`RemoveBgEditor__Canvas ${ready ? `RemoveBgEditor__Canvas--${tool}` : ''}`}
            onPointerDown={onPointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={handlePointerLeave}
            onWheel={onWheel}
        >
            {/* Loading state */}
            {status === REMOVE_BG_STATUS.PROCESSING && (
                <div className="RemoveBgEditor__Loading">
                    <Spinner color="primary" />
                    <p className="RemoveBgEditor__LoadingText">Processing image...</p>
                </div>
            )}

            {/* Canvas element */}
            {ready && (
                <canvas
                    ref={canvasRef}
                    className="RemoveBgEditor__CanvasElement"
                />
            )}

            {/* Brush cursor overlay */}
            {showBrushCursor && (
                <div
                    className="RemoveBgEditor__BrushCursor"
                    style={{
                        left: mousePos.x - cursorSize / 2,
                        top: mousePos.y - cursorSize / 2,
                        width: cursorSize,
                        height: cursorSize,
                    }}
                >
                    {mode === 'erase' ? (
                        <svg className="RemoveBgEditor__BrushCursorIcon" width={16} height={16} viewBox="0 0 16 16" fill="none">
                            <line x1="3" y1="8" x2="13" y2="8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    ) : (
                        <svg className="RemoveBgEditor__BrushCursorIcon" width={16} height={16} viewBox="0 0 16 16" fill="none">
                            <line x1="8" y1="3" x2="8" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                            <line x1="3" y1="8" x2="13" y2="8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    )}
                </div>
            )}

            {/* Error state */}
            {status === REMOVE_BG_STATUS.ERROR && (
                <div className="RemoveBgEditor__ErrorOverlay">
                    <p className="RemoveBgEditor__ErrorText">Failed to remove background</p>
                    {error?.message && (
                        <p className="RemoveBgEditor__ErrorDetail">{error.message}</p>
                    )}
                    {error?.message && /fetch|404|model|onnx|cdn/i.test(error.message) && (
                        <p className="RemoveBgEditor__ErrorHint">
                            Model is loaded from CDN. Check your network or try again later.
                        </p>
                    )}
                    <div className="RemoveBgEditor__ErrorActions">
                        <Button color="primary" outline onClick={retry}>
                            Retry
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default RemoveBgPreviewCanvas
